import { useCallback, useState } from "react";
import HeroSlider from "./HeroSlider";
import HeroVideo from "./HeroVideo";
import CyberBannerWeek2Slide from "./CyberBannerWeek2Slide";
import CyberSecWeek2Slider from "./CyberSecWeek2Slider";
import "./Hero1.css";
import HeroFilters from "./Herofilter";

// Expiry mirrors CyberBannerWeek2Slide + CyberSecWeek2Slider — both expire 20 Oct 2026.
// When expired Hero.jsx skips both straight to the main slider.
const CYBER_EXPIRY  = new Date("2026-10-20T23:59:59");
const cyberActive   = () => Date.now() <= CYBER_EXPIRY.getTime();

// Hero reel (when cyber campaign is live):
//   video → cyberbanner → cyber-slider → main-slider → video → …
//
// After 20 Oct 2026:
//   video → main-slider → video → …
//
// stage values:
//   "video"        — HeroVideo
//   "cyberbanner"  — CyberBannerWeek2Slide (full-bleed Week 2 artwork)
//   "cyber"        — CyberSecWeek2Slider  (5 educational slides)
//   "slides"       — HeroSlider      (main promo carousel)

export default function Hero() {
    const [stage,      setStage]      = useState("video");
    const [entrySlide, setEntrySlide] = useState(0);

    // Remount keys so each component restarts cleanly on re-entry
    const [videoRun,   setVideoRun]   = useState(0);
    const [bannerRun,  setBannerRun]  = useState(0);
    const [cyberRun,   setCyberRun]   = useState(0);
    const [sliderRun,  setSliderRun]  = useState(0);

    // ── Helpers ───────────────────────────────────────────────────────────────
    const openBanner = useCallback(() => {
        setBannerRun((n) => n + 1);
        setStage("cyberbanner");
    }, []);

    const openCyber = useCallback(() => {
        setCyberRun((n) => n + 1);
        setStage("cyber");
    }, []);

    const openSlider = useCallback((index) => {
        setEntrySlide(index);
        setSliderRun((n) => n + 1);
        setStage("slides");
    }, []);

    const backToVideo = useCallback(() => {
        setVideoRun((n) => n + 1);
        setStage("video");
    }, []);

    // ── Video callbacks ───────────────────────────────────────────────────────
    const handleVideoEnd = useCallback(() => {
        if (cyberActive()) { openBanner(); } else { openSlider(0); }
    }, [openBanner, openSlider]);

    const handleVideoNext = useCallback(() => {
        if (cyberActive()) { openBanner(); } else { openSlider(0); }
    }, [openBanner, openSlider]);

    // Stepping back from video → last main slide
    const handleVideoPrev = useCallback(() => {
        openSlider(-1);
    }, [openSlider]);

    // ── CyberBanner callbacks ─────────────────────────────────────────────────
    // Banner finished / arrow-right → into CyberSecSlider
    const handleBannerNext = useCallback(() => {
        openCyber();
    }, [openCyber]);

    // Arrow-left on banner → back to video
    const handleBannerRequestVideo = useCallback(() => {
        backToVideo();
    }, [backToVideo]);

    // ── CyberSec slider callbacks ─────────────────────────────────────────────
    // Finished all 6 slides → main slider
    const handleCyberEnd = useCallback(() => {
        openSlider(0);
    }, [openSlider]);

    // Arrow-left off slide 1 → back to banner
    const handleCyberRequestVideo = useCallback(() => {
        openBanner();
    }, [openBanner]);

    // ── Render ────────────────────────────────────────────────────────────────
    const isVideo  = stage === "video";
    const isBanner = stage === "cyberbanner";
    const isCyber  = stage === "cyber";
    const isSlider = stage === "slides";

    return (
        <>
            <section className={`hero ${isVideo ? "hero--video" : "hero--slider"}`}>

                {isVideo && (
                    <HeroVideo
                        key={videoRun}
                        onVideoEnd={handleVideoEnd}
                        onNext={handleVideoNext}
                        onPrev={handleVideoPrev}
                    />
                )}

                {isBanner && (
                    <CyberBannerWeek2Slide
                        key={bannerRun}
                        onNext={handleBannerNext}
                        onRequestVideo={handleBannerRequestVideo}
                    />
                )}

                {isCyber && (
                    <CyberSecWeek2Slider
                        key={cyberRun}
                        onCycleEnd={handleCyberEnd}
                        onRequestVideo={handleCyberRequestVideo}
                    />
                )}

                {isSlider && (
                    <HeroSlider
                        key={sliderRun}
                        initialIndex={entrySlide}
                        onCycleEnd={backToVideo}
                        onRequestVideo={backToVideo}
                    />
                )}

                <div className="overlay"></div>
            </section>

            <HeroFilters />
        </>
    );
}
